import React from 'react';
import { Check, Circle, CircleDot, Clock, BookOpen, Layers, GraduationCap, ArrowRight, Users, Target, Award } from 'lucide-react';
import './CourseOverview.css';

// Course overview and progress tracking shared by every course in the lesson
// player. A course may supply an `overview` object ({ promise, audience,
// outcomes, format, assessment }); without one, the overview falls back to
// the course description and the module objectives.

function moduleState(mod, progress, quizScores) {
  const total = mod.lessons.length;
  const done = mod.lessons.filter(l => progress[l.id]).length;
  const quizPassed = !mod.quiz || !!quizScores[mod.quiz.id]?.passed;
  if (done === total && quizPassed) return { status: 'complete', done, total };
  if (done > 0 || quizScores[mod.quiz?.id]) return { status: 'in-progress', done, total };
  return { status: 'not-started', done, total };
}

export function courseProgressStats(course, progress, quizScores) {
  const mods = course.modules || [];
  const lessons = mods.flatMap(m => m.lessons);
  const lessonsDone = lessons.filter(l => progress[l.id]).length;
  const quizzes = mods.filter(m => m.quiz).map(m => m.quiz.id);
  const quizzesPassed = quizzes.filter(id => quizScores[id]?.passed).length;
  const final = course.finalTest ? [course.finalTest.id] : [];
  const finalPassed = final.filter(id => quizScores[id]?.passed).length;
  // Each lesson, module check and the final assessment is one step.
  const steps = lessons.length + quizzes.length + final.length;
  const stepsDone = lessonsDone + quizzesPassed + finalPassed;
  const states = mods.map(m => moduleState(m, progress, quizScores));
  return {
    pct: steps ? Math.round(stepsDone / steps * 100) : 0,
    lessons: lessons.length, lessonsDone, modules: mods.length,
    modulesDone: states.filter(s => s.status === 'complete').length,
    states, hasFinal: final.length > 0, finalPassed: finalPassed > 0,
  };
}

function nextStep(course, progress, quizScores) {
  for (const mod of course.modules) {
    const lesson = mod.lessons.find(l => !progress[l.id]);
    if (lesson) return { mod, lesson };
    if (mod.quiz && !quizScores[mod.quiz.id]?.passed) return { mod, lesson: mod.lessons[mod.lessons.length - 1] };
  }
  return null;
}

export function CourseProgress({ course, progress, quizScores }) {
  const s = courseProgressStats(course, progress, quizScores);
  return (
    <div className="course-progress" aria-label="Course progress">
      <div className="course-progress-head">
        <span className="course-progress-pct">{s.pct}%</span>
        <span className="course-progress-label">complete</span>
      </div>
      <div className="course-progress-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={s.pct} aria-valuetext={`${s.pct}% complete`}>
        <div className="course-progress-fill" style={{ width: `${s.pct}%` }} />
      </div>
      <div className="course-progress-segments" aria-hidden="true">
        {s.states.map((st, i) => (
          <span key={i} className={`course-progress-seg course-progress-seg--${st.status}`} title={`${course.modules[i].title}: ${st.done}/${st.total} lessons`} />
        ))}
      </div>
      <p className="course-progress-meta">
        {s.lessonsDone} of {s.lessons} lessons · {s.modulesDone} of {s.modules} modules
        {s.hasFinal ? (s.finalPassed ? ' · final passed' : ' · final assessment to go') : ''}
      </p>
    </div>
  );
}

function dueText(assignment) {
  if (!assignment?.dueAt) return 'No due date has been set for you.';
  const date = new Date(assignment.dueAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  return `${assignment.kind === 'required' ? 'Required' : 'Targeted'} learning, due ${date}.`;
}

// The facts a learner needs before beginning, in one place and in plain words.
function BeforeYouStart({ course, stats, assignment }) {
  const passMark = course.finalTest?.passingScore ?? course.passMark ?? 75;
  const rows = [
    ['Purpose', course.overview?.promise || course.description],
    ['Time', course.duration || 'Not stated'],
    ['To complete', stats.hasFinal
      ? 'Finish every lesson and module check, then pass the final assessment.'
      : 'Finish every lesson and module check.'],
    ['Pass mark', stats.hasFinal || stats.modules ? `${passMark}% in each assessment.` : 'There is no assessment.'],
    ['Attempts', 'You can retake an assessment. Every attempt is recorded and your best result is kept.'],
    ['Deadline', dueText(assignment)],
    ['Certificate', stats.hasFinal
      ? 'Issued when you pass the final assessment. It confirms completion of this course and is not a formal academic qualification.'
      : 'This course does not issue a certificate.'],
  ];
  return (
    <section className="co-before" aria-labelledby="co-before-heading">
      <h3 id="co-before-heading">Before you start</h3>
      <dl>
        {rows.map(([label, text]) => (
          <div key={label}><dt>{label}</dt><dd>{text}</dd></div>
        ))}
      </dl>
    </section>
  );
}

export default function CourseOverview({ course, progress, quizScores, onStart, assignment }) {
  const o = course.overview || {};
  const s = courseProgressStats(course, progress, quizScores);
  const next = nextStep(course, progress, quizScores);
  const started = s.lessonsDone > 0;
  return (
    <div className="course-overview">
      <p className="co-eyebrow">{course.courseCode ? `${course.courseCode} · ` : ''}Course overview</p>
      <h2 className="co-title">{course.title}</h2>
      <p className="co-promise">{o.promise || course.description}</p>

      <div className="co-facts">
        {course.duration && <span><Clock size={15} strokeWidth={1.75} /> {course.duration}</span>}
        <span><Layers size={15} strokeWidth={1.75} /> {s.modules} modules</span>
        <span><BookOpen size={15} strokeWidth={1.75} /> {s.lessons} lessons</span>
        {course.level && <span><GraduationCap size={15} strokeWidth={1.75} /> {course.level}</span>}
        {course.delivery && <span><Users size={15} strokeWidth={1.75} /> {course.delivery === 'classroom' ? 'Classroom training, facilitator-led' : 'Self-paced online'}</span>}
      </div>

      <BeforeYouStart course={course} stats={s} assignment={assignment} />

      <div className="co-progress-card">
        <CourseProgress course={course} progress={progress} quizScores={quizScores} />
        {next ? (
          <button className="btn btn-primary co-start" onClick={() => onStart(next.mod, next.lesson)}>
            {started ? 'Continue where you left off' : 'Start the course'} <ArrowRight size={16} strokeWidth={1.75} />
          </button>
        ) : (
          <p className="co-done"><Award size={16} strokeWidth={1.75} /> All lessons and module checks are complete.{s.hasFinal && !s.finalPassed ? ' The final assessment is open in the sidebar.' : ''}</p>
        )}
      </div>

      {(o.audience?.length > 0 || o.outcomes?.length > 0) && (
        <div className="co-grid">
          {o.audience?.length > 0 && (
            <section className="co-panel">
              <h3><Users size={16} strokeWidth={1.75} /> Who it is for</h3>
              <ul>{o.audience.map(a => <li key={a}>{a}</li>)}</ul>
            </section>
          )}
          {o.outcomes?.length > 0 && (
            <section className="co-panel">
              <h3><Target size={16} strokeWidth={1.75} /> By the end you will be able to</h3>
              <ul>{o.outcomes.map(a => <li key={a}>{a}</li>)}</ul>
            </section>
          )}
        </div>
      )}

      <section className="co-roadmap">
        <h3>What the course covers</h3>
        <ol className="co-modules">
          {course.modules.map((mod, i) => {
            const st = s.states[i];
            return (
              <li key={mod.id} className={`co-module co-module--${st.status}`}>
                <button className="co-module-btn" onClick={() => onStart(mod, mod.lessons.find(l => !progress[l.id]) || mod.lessons[0])}>
                  <span className="co-module-icon" aria-hidden="true">
                    {st.status === 'complete' ? <Check size={15} strokeWidth={2} /> : st.status === 'in-progress' ? <CircleDot size={15} strokeWidth={2} /> : <Circle size={15} strokeWidth={1.75} />}
                  </span>
                  <span className="co-module-body">
                    <span className="co-module-title">{mod.title}</span>
                    {mod.objective && <span className="co-module-obj">{mod.objective}</span>}
                    <span className="co-module-meta">
                      {mod.lessons.length} lesson{mod.lessons.length === 1 ? '' : 's'}{mod.quiz ? ' · module check' : ''} · {st.status === 'complete' ? 'Complete' : st.status === 'in-progress' ? `${st.done} of ${st.total} lessons done` : 'Not started'}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </section>

      {(o.assessment?.length > 0 || o.format?.length > 0) && (
        <div className="co-grid">
          {o.format?.length > 0 && (
            <section className="co-panel">
              <h3><Clock size={16} strokeWidth={1.75} /> How it is delivered</h3>
              <ul>{o.format.map(a => <li key={a}>{a}</li>)}</ul>
            </section>
          )}
          {o.assessment?.length > 0 && (
            <section className="co-panel">
              <h3><Award size={16} strokeWidth={1.75} /> Assessment and certificate</h3>
              <ul>{o.assessment.map(a => <li key={a}>{a}</li>)}</ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
